import React, { useEffect, useRef, useState } from 'react';
import './About.scss';
import { Award, ChevronLeft, ChevronRight, Phone, Shield, Sparkle } from '../components/Icons';

const videos = [
  'https://www.youtube.com/embed/RFA4cV7LY0U?autoplay=1&controls=1&mute=1',
  'https://www.youtube.com/embed/Nyd9jtvJMWs?autoplay=1&controls=1&mute=1',
];

const millions = (n: number) => (n % 10 === 0 ? String(n / 10) : (n / 10).toFixed(1));

const stats = [
  { value: 425, suffix: '+', title: 'Happy customers' },
  { value: 20, suffix: 'M+', format: millions, title: 'Sq ft painted' },
  { value: 10, suffix: '+', title: 'Years in Delta' },
  { value: 100, suffix: '%', title: 'Satisfaction guarantee' },
];

type CounterProps = { value: number; suffix: string; format?: (n: number) => string };

/** Counts up from 0 when it scrolls into view. */
function Counter({ value, suffix, format = String }: CounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [n, setN] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        if (document.visibilityState === 'hidden') {
          setN(value);
          return;
        }
        const start = performance.now();
        const dur = 1400;
        const tick = (now: number) => {
          const t = Math.min(1, Math.max(0, (now - start) / dur));
          const eased = 1 - Math.pow(1 - t, 3);
          setN(Math.round(value * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value]);

  return (
    <span ref={ref} className="stat__value">
      {format(n)}
      {suffix}
    </span>
  );
}

const d = (ms: number) => ({ '--delay': `${ms}ms` } as React.CSSProperties);

export function About() {
  const [index, setIndex] = useState(0);
  const prev = () => setIndex((i) => (i > 0 ? i - 1 : videos.length - 1));
  const next = () => setIndex((i) => (i < videos.length - 1 ? i + 1 : 0));

  return (
    <section id="about" className="section about">
      <div className="container about__grid">
        <div className="about__copy">
          <span className="eyebrow reveal">Who we are</span>
          <h2 className="reveal" style={d(80)}>
            Experienced.
            <br />
            Reliable.
            <br />
            Local.
          </h2>
          <p className="about__text reveal" style={d(160)}>
            Ivy League Painting is a full-service painting company based in Delta, BC. For over 10 years we've been
            keeping Delta's homes looking their best, with 425+ happy customers along the way.
          </p>
          <p className="about__text reveal" style={d(220)}>
            From humble beginnings painting and power-washing the neighbourhoods of Delta, we now paint homes,
            strata buildings, commercial spaces and warehouses across the Lower Mainland. Our customers'
            satisfaction stays at the forefront: we carry $5 million in liability insurance, and every job comes
            with a 3-year, 100% satisfaction guarantee.
          </p>
          <div className="about__badges reveal" style={d(280)}>
            <span className="badge">
              <Shield /> Licensed &amp; Insured
            </span>
            <span className="badge">
              <Award /> 10+ Years Exp
            </span>
            <span className="badge">
              <Sparkle /> BBB A+
            </span>
          </div>
          <a className="btn btn--blue reveal" style={d(340)} href="tel:+16043655225">
            <Phone /> Call our office now
          </a>
        </div>

        <div className="about__media reveal" style={d(120)}>
          <div className="about__frame">
            <iframe
              key={index}
              src={videos[index]}
              title="Ivy League Painting video"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <div className="about__since">
            <strong>10+</strong>
            <span>years in Delta</span>
          </div>
          <div className="about__controls">
            <button className="icon-btn" onClick={prev} aria-label="Previous video">
              <ChevronLeft />
            </button>
            <button className="icon-btn" onClick={next} aria-label="Next video">
              <ChevronRight />
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        <ul className="stats">
          {stats.map((s, i) => (
            <li key={s.title} className="stat reveal" style={d(i * 80)}>
              <Counter value={s.value} suffix={s.suffix} format={s.format} />
              <span className="stat__title">{s.title}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
